import { Form, Input, Button, Calendar } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "../../i18n";

const { TextArea } = Input;

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => hour);
const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, index) => index * 5);
const DEFAULT_HOUR = 10;
const DEFAULT_MINUTE = 0;

function pad2(value) {
    return String(value).padStart(2, "0");
}

function toDayjs(value) {
    if (!value) {
        return null;
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
}

/** 内嵌日期时间选择：日历与时分直接铺在表单里，避免移动端弹出层难点。 */
function InlineShootAtPicker({ value, onChange }) {
    const { t } = useTranslation();
    const current = toDayjs(value);

    const emit = (next) => {
        onChange?.(next.second(0).millisecond(0));
    };

    const handleDateSelect = (date) => {
        if (!date) {
            return;
        }
        const next = (current || dayjs().hour(DEFAULT_HOUR).minute(DEFAULT_MINUTE))
            .year(date.year())
            .month(date.month())
            .date(date.date());
        emit(next);
    };

    const handleHourChange = (event) => {
        const hour = Number(event.target.value);
        if (!Number.isFinite(hour)) {
            return;
        }
        const base = current || dayjs().minute(DEFAULT_MINUTE);
        emit(base.hour(hour));
    };

    const handleMinuteChange = (event) => {
        const minute = Number(event.target.value);
        if (!Number.isFinite(minute)) {
            return;
        }
        const base = current || dayjs().hour(DEFAULT_HOUR);
        emit(base.minute(minute));
    };

    return (
        <div className="booking-form__datetime">
            <Calendar
                fullscreen={false}
                value={current || undefined}
                onSelect={handleDateSelect}
            />
            <div className="booking-form__time-row">
                <label className="booking-form__time-field">
                    <span className="booking-form__time-label">{t("booking.form.shootAtHour")}</span>
                    <select
                        className="booking-form__time-select"
                        value={current ? current.hour() : ""}
                        onChange={handleHourChange}
                        aria-label={t("booking.form.shootAtHour")}
                    >
                        <option value="" disabled>
                            --
                        </option>
                        {HOUR_OPTIONS.map((hour) => (
                            <option key={hour} value={hour}>
                                {pad2(hour)}
                            </option>
                        ))}
                    </select>
                </label>
                <span className="booking-form__time-sep" aria-hidden="true">
                    :
                </span>
                <label className="booking-form__time-field">
                    <span className="booking-form__time-label">{t("booking.form.shootAtMinute")}</span>
                    <select
                        className="booking-form__time-select"
                        value={current ? current.minute() - (current.minute() % 5) : ""}
                        onChange={handleMinuteChange}
                        aria-label={t("booking.form.shootAtMinute")}
                    >
                        <option value="" disabled>
                            --
                        </option>
                        {MINUTE_OPTIONS.map((minute) => (
                            <option key={minute} value={minute}>
                                {pad2(minute)}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
        </div>
    );
}

function BookingForm({ onSubmit, submitting }) {
    const { t } = useTranslation();
    const [form] = Form.useForm();

    const handleFinish = (values) => {
        onSubmit?.(values);
    };

    return (
        <Form form={form} layout="vertical" onFinish={handleFinish} requiredMark={false}>
            <Form.Item
                name="title"
                label={t("booking.form.title")}
                rules={[{ required: true, message: t("booking.form.titleRequired") }]}
            >
                <Input placeholder={t("booking.form.titlePlaceholder")} maxLength={120} />
            </Form.Item>

            <Form.Item
                name="location"
                label={t("booking.form.location")}
                rules={[{ required: true, message: t("booking.form.locationRequired") }]}
            >
                <Input placeholder={t("booking.form.locationPlaceholder")} maxLength={200} />
            </Form.Item>

            <Form.Item
                name="shootAt"
                label={t("booking.form.shootAt")}
                extra={t("booking.form.shootAtLocalHint")}
                rules={[{ required: true, message: t("booking.form.shootAtRequired") }]}
            >
                <InlineShootAtPicker />
            </Form.Item>

            <Form.Item name="duration" label={t("booking.form.duration")}>
                <Input placeholder={t("booking.form.durationPlaceholder")} maxLength={80} />
            </Form.Item>

            <Form.Item name="content" label={t("booking.form.content")}>
                <TextArea
                    rows={4}
                    placeholder={t("booking.form.contentPlaceholder")}
                    maxLength={4000}
                    showCount
                />
            </Form.Item>

            <Form.Item name="notes" label={t("booking.form.notes")}>
                <TextArea
                    rows={3}
                    placeholder={t("booking.form.notesPlaceholder")}
                    maxLength={4000}
                    showCount
                />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" loading={submitting} block>
                    {t("booking.form.submit")}
                </Button>
            </Form.Item>
        </Form>
    );
}

export default BookingForm;
